import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Plus, Trash2, Users, ArrowUpRight } from "lucide-react";

export default function ClubTeams() {
  const [teams, setTeams] = useState([]);
  const [cats, setCats] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", category_id: "", coach_ids: [], season: "2025-2026" });

  const load = async () => {
    try {
      const [t, c, u] = await Promise.all([
        api.get("/teams"),
        api.get("/categories"),
        api.get("/users"),
      ]);
      setTeams(t.data);
      setCats(c.data);
      setCoaches(u.data.filter((x) => x.role === "coach"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/teams", form);
      toast.success("Equipo creado");
      setShowForm(false);
      setForm({ name: "", category_id: "", coach_ids: [], season: "2025-2026" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar equipo y sus jugadores?")) return;
    try {
      await api.delete(`/teams/${id}`);
      toast.success("Equipo eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleCoach = (id) => {
    setForm((f) => ({
      ...f,
      coach_ids: f.coach_ids.includes(id) ? f.coach_ids.filter((x) => x !== id) : [...f.coach_ids, id],
    }));
  };

  const getCat = (id) => cats.find((c) => c.id === id);
  const getCoach = (id) => coaches.find((c) => c.id === id);

  return (
    <div>
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="overline">Estructura del club</div>
          <h1 className="font-heading font-black text-5xl tracking-tighter uppercase mt-2">Equipos</h1>
          <p className="text-muted-foreground text-sm mt-2">Da de alta equipos y asígnales entrenadores.</p>
        </div>
        <button data-testid="new-team-btn" onClick={() => setShowForm(true)} className="btn-volt">
          <Plus size={16} /> Nuevo equipo
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {teams.map((t) => {
          const cat = getCat(t.category_id);
          return (
            <div key={t.id} data-testid={`team-card-${t.id}`} className="card-tactical p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="overline">{cat?.name || "—"} · {cat?.template_key}</div>
                  <div className="font-heading font-black text-2xl uppercase tracking-tighter mt-1">{t.name}</div>
                </div>
                <button data-testid={`del-team-${t.id}`} onClick={() => remove(t.id)} className="text-muted-foreground hover:text-destructive">
                  <Trash2 size={15} />
                </button>
              </div>
              <div className="mt-4 pt-4 border-t border-border">
                <div className="overline">Entrenadores</div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {t.coach_ids.map((cid) => {
                    const c = getCoach(cid);
                    return c ? (
                      <span key={cid} className="px-2 py-1 bg-secondary text-xs font-mono">{c.name}</span>
                    ) : null;
                  })}
                  {t.coach_ids.length === 0 && <span className="text-xs text-muted-foreground">Sin asignar</span>}
                </div>
              </div>
              <Link to={`/team/${t.id}`} className="mt-4 flex items-center justify-between border-t border-border pt-3 text-primary text-sm font-heading uppercase tracking-wider">
                Ver equipo <ArrowUpRight size={14} />
              </Link>
            </div>
          );
        })}
        {teams.length === 0 && (
          <div className="col-span-full text-center py-12 border border-dashed border-border">
            <Users className="mx-auto text-muted-foreground" size={28} />
            <div className="mt-3 text-muted-foreground text-sm">No hay equipos aún.</div>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-card border border-border w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div>
              <div className="overline">Nuevo</div>
              <div className="font-heading font-black text-2xl uppercase tracking-tight">Añadir equipo</div>
            </div>
            <div>
              <label className="overline block mb-1">Nombre *</label>
              <input required data-testid="team-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Alevín A" className="w-full h-10 bg-transparent border border-border px-3 focus:border-primary focus:outline-none font-mono text-sm" />
            </div>
            <div>
              <label className="overline block mb-1">Categoría *</label>
              <select required data-testid="team-cat-select" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="w-full h-10 bg-card border border-border px-3 focus:border-primary focus:outline-none font-mono text-sm">
                <option value="">— Selecciona —</option>
                {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-1">Temporada</label>
              <input value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm" />
            </div>
            <div>
              <label className="overline block mb-2">Entrenadores</label>
              <div className="space-y-2 max-h-48 overflow-y-auto border border-border p-3">
                {coaches.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" data-testid={`coach-check-${c.id}`} checked={form.coach_ids.includes(c.id)} onChange={() => toggleCoach(c.id)} className="accent-primary" />
                    <span className="text-sm">{c.name} <span className="text-muted-foreground text-xs">· {c.email}</span></span>
                  </label>
                ))}
                {coaches.length === 0 && <div className="text-xs text-muted-foreground">Ningún entrenador registrado.</div>}
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="h-10 px-5 border border-border text-sm uppercase font-heading tracking-wider">Cancelar</button>
              <button type="submit" data-testid="submit-team-btn" className="btn-volt">Crear equipo</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
